import React from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  cascadeStandardsOperationsAtom,
  cascadeStandardsMetricsAtom,
} from './atoms'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import { doTreeOperationsJob } from '../../contentService/utility'

interface CascadeStandardsProps {
  standardsSelected: string
  treeId: string
  versionId: string
}

export function CascadeStandards({
  standardsSelected,
  treeId,
  versionId,
}: CascadeStandardsProps) {
  const operations = useAtomValue(cascadeStandardsOperationsAtom)
  const {
    totalLeavesToAdd,
    skillListCount,
    nodesToUpdate,
    hasQuestionsNeedingSkillLists,
  } = useAtomValue(cascadeStandardsMetricsAtom)

  const startFlowFunction = async (): Promise<string> => {
    const result = await doTreeOperationsJob(operations)
    if (!result.result?.data?.flow_id) {
      throw new Error('No flow ID received from API')
    }
    return result.result.data.flow_id
  }

  return (
    <Paper elevation={2} sx={{ padding: 2, marginTop: 2 }}>
      <Typography variant="h6" gutterBottom>
        Cascade Standards
      </Typography>
      <Box sx={{ marginTop: 1 }}>
        <Typography>
          <strong>Nodes to be updated:</strong> {nodesToUpdate}
        </Typography>
        <Typography>
          <strong>Skill Lists that would be updated:</strong> {skillListCount}
        </Typography>
        <Typography>
          <strong>Total skills that would be added:</strong> {totalLeavesToAdd}
        </Typography>
        {nodesToUpdate === 0 && (
          <Typography color="text.secondary" sx={{ marginTop: 1 }}>
            No questions available for cascading standards.
          </Typography>
        )}
        {hasQuestionsNeedingSkillLists && (
          <Typography color="warning.main" sx={{ marginTop: 1 }}>
            Please create missing skill lists first before cascading standards.
          </Typography>
        )}
        <Typography
          color="text.secondary"
          sx={{ marginTop: 1, fontStyle: 'italic' }}
        >
          Reset Tree when this operation is completed.
        </Typography>
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <StartAndMonitorFlow
          disabled={nodesToUpdate === 0 || hasQuestionsNeedingSkillLists}
          buttonText="Cascade Standards"
          processingText="Cascading Standards..."
          startFlowFunction={startFlowFunction}
          pollingIntervalMs={2000}
          downloadResults={true}
        />
      </Box>
    </Paper>
  )
}
